import {Component, OnInit, ViewChild} from '@angular/core';
import {Grid} from '../../../../shared/Models/grid';
import {ActionColumn, ActionItem, TextColumn} from '../../../../shared/Models/columns';
import {SystemUser} from '../../../../shared/Models/SystemUser';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {AuthenticationService} from '../../../../shared/Services/authentication.service';
import {NgbModal, NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {DealerUserService} from '../../../Services/dealer-user.service';
import {BroadcastService} from '../../../../shared/Services/broadcast.service';
import {ChangePassword} from '../../../Models/ChangePassword';
import {throwError} from 'rxjs';
import {Helper} from '../../../../helpers/helper.service';
import {catchError} from 'rxjs/operators';
import {DealerRole} from '../../../Models/DealerRole';

@Component({
  selector: 'app-account-overview',
  templateUrl: './account-overview.component.html',
  styleUrls: ['./account-overview.component.scss']
})
export class AccountOverviewComponent implements OnInit {
  public grid: Grid;
  public user: SystemUser;
  public editingUser;
  public manageProfile;
  public changePasswordModel: ChangePassword = new ChangePassword();
  public showManageUserModal = false;
  public totalUsers;
  public refreshModal;
  public fullName;
  public userId;
  public archive = false;
  public isAdmin = false;
  public allowItems = [];
  public isAllowedPartialPaid: any;
  @ViewChild('changePasswordModal', {static: false}) changePasswordModal;
  @ViewChild('changePasswordForm', {static: false}) changePasswordForm;
  @ViewChild('deleteModal', {static: false}) deleteConfirm;
  @ViewChild('reAddModal', {static: false}) reAddConfirm;
  @ViewChild('allowPartialOrderModal', {static: false}) allowPartialOrderModal;

  constructor(
    private router: Router,
    private userService: DealerUserService,
    private auth: AuthenticationService,
    private modalService: NgbModal,
    private broadcastService: BroadcastService,
    private helper: Helper,
    private translator: TranslateService) {
  }

  ngOnInit() {
    this.allowItems.push({label: this.translator.instant('doctrine.user.allow_partial_order'), value: true});
    this.allowItems.push({label: this.translator.instant('doctrine.user.not_allow_partial_order'), value: false});

    this.isAdmin = this.auth.isDealerAdmin();
    this.getProfile();
    this.grid = <Grid>{
      columns: [
        <TextColumn>{
          name: 'doctrine.user.fullName',
          prop: 'fullName',
          sortable: false,
          type: TextColumn.type,
          translate: true
        },
        <TextColumn>{
          name: 'doctrine.user.username',
          prop: 'username',
          sortable: false,
          type: TextColumn.type,
          translate: true,
          responsiveHideCallback: (width) => width < 1024
        },
        <TextColumn>{
          name: 'doctrine.user.email',
          prop: 'email',
          sortable: false,
          type: TextColumn.type,
          translate: true
        },
        <TextColumn>{
          name: 'doctrine.user.role',
          prop: 'role',
          sortable: false,
          type: TextColumn.type,
          translate: true,
          valueFormatter: role => role.name ? this.translator.instant(role.name) : ''
        },
        <TextColumn>{
          name: 'doctrine.user.status',
          prop: 'status',
          sortable: false,
          type: TextColumn.type,
          translate: true,
          valueFormatter: row => {
            switch (row) {
              case SystemUser.SYSTEMUSER_ACTIVE:
                return this.translator.instant('global.active');
              case SystemUser.SYSTEMUSER_INACTIVE:
                return this.translator.instant('global.inactive');
              case SystemUser.SYSTEMUSER_ON_HOLD:
                return this.translator.instant('global.on_hold');
            }
          }
        },
        <ActionColumn>{
          name: 'global.actions',
          prop: null,
          type: ActionColumn.type,
          sortable: false,
          translate: true,
          width: 100,
          actionItems: [
            <ActionItem>{
              icon: 'fa fa-pencil fa-lg',
              tooltip: 'global.edit',
              onclick: row => this.openManageUserModal(row, false),
              showCallback: row => !this.archive && this.auth.isDealerAdmin(),
              btnClass: 'ripple light ms-1'
            },
            <ActionItem>{
              icon: 'fa fa-remove fa-lg',
              tooltip: 'global.remove',
              onclick: row => {
                this.fullName = '<strong>' + row.fullName + '</strong>';
                this.userId = row.id;
                return this.deleteConfirm.show();
              },
              showCallback: row => !this.archive && row.role.name !== DealerRole.ROLE_ADMIN && row.parent,
              btnClass: 'ripple light ms-1'
            },
            <ActionItem>{
              icon: 'fa fa-plus fa-lg',
              tooltip: 'global.readd',
              onclick: row => {
                this.fullName = '<strong>' + row.fullName + '</strong>';
                this.userId = row.id;
                return this.reAddConfirm.show();
              },
              showCallback: row => this.archive && this.auth.isDealerAdmin(),
              btnClass: 'ripple light ms-1'
            },
          ]
        }
      ],
      service: this.userService,
      rememberURL: this.router.url,
      method: 'getUserEmployee',
      defaultSort: 'firstName',
      rowDetailShowCallback: (row, size) => false,
      defaultSortType: 'desc',
      showButton: false,
      archive: this.archive
    };
  }

  getProfile() {
    this.auth.getProfile().subscribe(res => {
      this.user = res;
    });
  }

  openManageUserModal(user, isManageProfile) {
    this.refreshModal = Math.random();
    this.manageProfile = isManageProfile;
    this.editingUser = Object.assign({}, user);
    this.showManageUserModal = true;
    this.broadcastService.broadcast('refreshModal', this.editingUser);
  }

  setTotalUsersCount($event) {
    this.totalUsers = $event;
  }

  refreshGrid($event) {
    if (this.auth.isDealerAdmin()) {
      this.getProfile();
      this.broadcastService.broadcast('refreshGrid', 'getUserEmployee');
    }
  }

  softDelete() {
    this.userService.softDeleteDealerUser(this.userId).subscribe(res => {
      this.deleteConfirm.hide();
      this.broadcastService.broadcast('refreshGrid', 'getUserEmployee');
    });
  }

  switchShowArchive() {
    this.archive = !this.archive;
    this.grid.archive = this.archive;
    this.broadcastService.broadcast('refreshGrid', 'getUserEmployee');
  }

  reAdd() {
    this.userService.reAddDealerUser(this.userId).subscribe(res => {
      this.reAddConfirm.hide();
      this.archive = false;
      this.grid.archive = this.archive;
      this.broadcastService.broadcast('refreshGrid', 'getUserEmployee');
    });
  }

  changePassword() {
    if (this.changePasswordModel.password !== this.changePasswordModel.passwordConfirmation) {
      this.helper.popMessageDialog(null, this.translator.instant('password_and_confirm_not_equal'));
    }
    this.userService.changePassword(this.changePasswordModel).pipe(catchError(res => this.catchError(res))).subscribe(res => {
      if (res) {
        this.helper.popMessageDialog(
          this.translator.instant('global.success_message.title'),
          this.translator.instant('admin.dealer.account.password_changed'),
          'success',
          false
        );
      }
      this.changePasswordForm.reset();
      this.changePasswordModal.hide();
    });
  }

  catchError(response) {
    const body: any = response.error;
    this.helper.popMessageDialog(this.translator.instant('auth.password_is_invalid'), this.translator.instant('auth.password_is_invalid'));
    this.changePasswordForm.reset();
    return throwError(body.errors);
  }

  openManageAllowPartialOrderModal(isAllowedPartialPaid, userId) {
    this.userId = userId;
    const label = isAllowedPartialPaid ? this.translator.instant('doctrine.user.allow_partial_order') : this.translator.instant('doctrine.user.not_allow_partial_order');
    this.isAllowedPartialPaid = {label: label, value: isAllowedPartialPaid};
    this.allowPartialOrderModal.show();
  }

  manageAllowPartialPaid() {
    this.userService.manageAllowPartialOrder(this.isAllowedPartialPaid, this.userId).subscribe(res => {
      this.getProfile();
      this.allowPartialOrderModal.hide();
    });
  }

}
